import { setMode } from "@stencil/core";
import _ from "lodash";
export const defaultTheme = 'default';
/** The local storage key for storing the user selected theme */
export const storedThemeKey = 'themeName';
/**
 * The attribute for annotating the component theme to element. E.g. <mo-button theme="dark" />
 *
 * This will only be applied to components with mode styles defined. For example,
 *
 * @Component({
 *   tag: 'mo-select',
 *
 *   // Define styles for different theme modes.
 *   styleUrls: {
 *     light: 'mo-select.light.scss',
 *     dark: 'mo-select.dark.scss',
 *     ...
 *   },
 * }
 */
export const elementThemeAttr = 'theme';
/**
 * Initial theming setup which is invoked by the stencil global script.
 * This should be applied to all components from both common and stencil projects.
 */
export const initTheming = (customThemeResolver) => {
    // Guard the case when it's invoked after window is unloaded for page redirect.
    if (document.body) {
        // Retrieve the persisted theme.
        setTheme(getTheme(), false);
        // Register mode resolution handler. It will be invoked during `connectedCallback()` if the
        // the component has mode styles defined via `styleUrls`.
        setMode(customThemeResolver || themeResolver);
    }
};
/**
 * For setting global theme on app. All stencil components with the target theme mode defined will be
 * refreshed accordingly.
 *
 * This would also be the entry point for syncing up theming between common components and non-stencil app.
 */
export const setTheme = (theme, reload = true) => {
    const storedTheme = localStorage[storedThemeKey];
    // Register the current theme to body element for the global selector to target on.
    document.body.setAttribute(elementThemeAttr, theme);
    if (storedTheme !== theme) {
        localStorage[storedThemeKey] = theme;
        if (reload && !_.isNil(storedTheme)) {
            // REVIEW: Hard refresh to force component DOM reconnection as the most stable way for switching.
            // Will see if there's an elegant way for re-connecting components (multi-themes components only) on-the-fly.
            window.location.href = String(window.location.href);
        }
    }
};
/**
 * For getting global theme set on app.
 *
 * It returns the default theme if there's no entry persisted in localStorage, or if the `theme-disabled`
 * attribute is set to the body element.
 */
export const getTheme = () => {
    const themeDisabled = document.body.getAttribute('theme-disabled') === 'true';
    return !themeDisabled && localStorage[storedThemeKey] || defaultTheme;
};
/**
 * Dynamically apply custom style on-the-fly.
 *
 * For example,
 *
 * applyStyles(`
 *   mo-select {
 *     --bgColor: #ccc;
 *   }
 *
 *   mo-select[theme=dark] {
 *     --bgColor: #333;
 *   }
 * `);
 */
export const applyStyles = (content) => {
    // TODO: Add a proper css validator and error out in console for invalid styles.
    const style = document.createElement('style');
    style.appendChild(document.createTextNode(content));
    document.head.appendChild(style);
};
/**
 * Default theme resolver: Use the specific theme if the theme attribute is explicitly set to the
 * component instance e.g. `<mo-select theme="light" />`, or fall back to use the global theme.
 */
const themeResolver = (element) => {
    const elementTheme = element[elementThemeAttr] || element.getAttribute(elementThemeAttr);
    const hasElementTheme = !_.isEmpty(elementTheme) && elementTheme !== 'undefined';
    const theme = (hasElementTheme && elementTheme) || getTheme();
    if (!hasElementTheme) {
        element.setAttribute(elementThemeAttr, theme);
    }
    return theme;
};
